/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Card from "@mui/material/Card";
import MDBox from "../../../components/MDBox";
import MDTypography from "../../../components/MDTypography";
import MDInput from "../../../components/MDInput";
import MDButton from "../../../components/MDButton";
import BasicLayout from "../components/BasicLayout";
import bgImage from "../../../assets/images/bg-sign-in-basic.jpeg";
import {AUTH_URL, PERSONAS_URL} from "../../../config";
import { useCimbeo } from "../../../context";

function SignIn() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { setToken, setUser } = useCimbeo();

  const verifyUser = async() => {
    console.log('Attempting to verify user...');
    const authURL = `${AUTH_URL}/v1/auth/signin`
    console.log('Auth URL:', authURL);
    
    const resp = await fetch(authURL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({username, password})
    })

    if (!resp.ok) {
      console.error('Sign in failed with status:', resp.status);
      switch (resp.status) {
        case 400:
          throw new Error("Invalid request. Please check your input.");
        case 401:
          throw new Error("User combination not found. Invalid username or password.");
        case 500:
          throw new Error("Server error. Please try again later.");
        default:
          throw new Error("An error occurred. Please try again.");
      }
    }

    const data = await resp.json()
    console.log('Sign in response data:', data);
    
    if (!data.token || !data.exp_time) {
      console.error('Missing token or expiration time in response:', data);
      throw new Error("Invalid response from server - missing token data");
    }

    console.log('Setting token with:', {
      token: data.token,
      expiration: data.exp_time
    });
    
    setToken({
      token: data.token,
      expiration: data.exp_time
    });
    return data.token
  }

  const getUserData = async(userJWT) => {
    console.log("Getting user data with token:", userJWT);
    const personasUrl = `${PERSONAS_URL}/profile`
    const resp = await fetch(personasUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userJWT}`
      },
    })

    if (!resp.ok) {
      console.error('Failed to get user data:', resp.status);
      switch (resp.status) {
        case 401:
          throw new Error("Invalid user token"); 
        case 404:
          throw new Error("User data not found");
        case 500:
          throw new Error("Server error. Please try again later.");
        default:
          throw new Error("An error occurred. Please try again.");
      }
    }

    const data = await resp.json()
    console.log("User data received:", data);

    setUser({
      id: data.user_id,
      username: username,
      firstName: data.first_name, 
      lastName: data.last_name,
      email: data.email,
      phone: data.phone_number,
      photo: null,
      organization: data.organization,
      department: data.job_title
    })
    console.log("User data set successfully");
  }
  
  const handleSubmit = async () => {
    console.log('Starting sign in process...');
    setError(""); // Clear any previous errors
  
    try {
      const newToken = await verifyUser()
      console.log('Token received, getting user data...');

      await getUserData(newToken)
      console.log('User data received, navigating to home...');

      navigate("/home")
    } catch (err) {
      console.error('Sign in error:', err);
      setError(err.message);
    }
  }

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="navy"
          borderRadius="lg"
          coloredShadow="navy"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" mt={1}>
            Sign in
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form">
            {error && (
              <MDBox mb={2}>
                <MDTypography variant="caption" color="error" fontWeight="light" textAlign="center" display="block">
                  {error}
                </MDTypography>
              </MDBox>
            )}
            <MDBox mb={2}>
              <MDInput 
                type="text" 
                label="Username" 
                fullWidth 
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput 
                type="password" 
                label="Password" 
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton variant="gradient" fullWidth onClick={handleSubmit}>
                sign in
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default SignIn;
